import Box from '@totallymoney/ui/components/Box'
import Stack from '@totallymoney/ui/components/Stack'
import TrustBox from '@totallymoney/ui/components/TrustBox'
import theme from '@totallymoney/ui/theme'

import { CreditCardProductCategory } from '@appTypes/EligibilityApi.interface'

import CategoryPageAboutSection from '@components/UnscoredCategoryPageComponents/UnscoredCategoryPageAboutSection'
import CategoryPageApplySection from '@components/UnscoredCategoryPageComponents/UnscoredCategoryPageApplySection'
import {
    UnscoredCategoryPageBackgroundTextureCardTypesSection,
    UnscoredCategoryPageBackgroundTextureExpertSection,
    UnscoredCategoryPageBackgroundTextureProsAndConsSection,
    UnscoredCategoryPageBackgroundTextureReviewsSection,
} from '@components/UnscoredCategoryPageComponents/UnscoredCategoryPageBackgroundTextures'
import CategoryPageExpertSection from '@components/UnscoredCategoryPageComponents/UnscoredCategoryPageExpertSection'
import CategoryPageFaqsSection from '@components/UnscoredCategoryPageComponents/UnscoredCategoryPageFaqsSection'
import CategoryPageFindingTheRightCardSection from '@components/UnscoredCategoryPageComponents/UnscoredCategoryPageFindingTheRightCardSection'
import CategoryPageHero from '@components/UnscoredCategoryPageComponents/UnscoredCategoryPageHero'
import CategoryPageNavigation from '@components/UnscoredCategoryPageComponents/UnscoredCategoryPageNavigation'
import CategoryPageProsAndConsSection from '@components/UnscoredCategoryPageComponents/UnscoredCategoryPageProsAndConsSection'
import CategoryPageProvidersSection from '@components/UnscoredCategoryPageComponents/UnscoredCategoryPageProvidersSection'
import CategoryPageRelatedArticles from '@components/UnscoredCategoryPageComponents/UnscoredCategoryPageRelatedArticleSection'
import CategoryPageReviewsSection from '@components/UnscoredCategoryPageComponents/UnscoredCategoryPageReviewsSection'
import CategoryPageSectionWrapper from '@components/UnscoredCategoryPageComponents/UnscoredCategoryPageSectionWrapper'
import UnscoredCategoryPageSeo from '@components/UnscoredCategoryPageComponents/UnscoredCategoryPageSeo'
import CategoryPageTypesSection from '@components/UnscoredCategoryPageComponents/UnscoredCategoryPageTypesSection'
import CategoryPageWhyTotallyMoneySection from '@components/UnscoredCategoryPageComponents/UnscoredCategoryPageWhyTotallyMoneySection'

const UnscoredRewardsPage = () => {
    const page = CreditCardProductCategory.Rewards

    return (
        <>
            <UnscoredCategoryPageSeo
                title="Compare rewards credit cards and check your offers"
                description="Compare rewards credit cards and earn points or cashback on your spending. Check your eligibility with no impact on your credit score."
            />

            <Box as="main">
                <CategoryPageHero
                    bullets={[
                        'Compare rewards credit cards',
                        'See your best offers',
                        'Check your eligibility',
                        'Protect your credit score',
                    ]}
                    title="Compare rewards credit cards"
                />

                <Box mb="space20">
                    <TrustBox />
                </Box>

                <CategoryPageNavigation />

                <Stack space={0}>
                    <CategoryPageSectionWrapper>
                        <CategoryPageProvidersSection />
                    </CategoryPageSectionWrapper>

                    <CategoryPageSectionWrapper
                        backgroundColor={`linear-gradient(180deg, ${theme.neutral80} 0%, transparent 100%)`}
                        hasWave
                        id="about-credit-cards"
                        title="About rewards credit cards"
                        waveColor={theme.neutral80}
                    >
                        <CategoryPageAboutSection page={page} />
                    </CategoryPageSectionWrapper>

                    <CategoryPageSectionWrapper
                        backgroundColor={`linear-gradient(180deg, ${theme.indigo10} 0%, transparent 100%)`}
                        title="How to apply for a rewards credit card"
                        hasWave
                        waveColor={theme.indigo10}
                    >
                        <CategoryPageApplySection
                            page={page}
                            title="How to apply for a rewards credit card"
                        />
                    </CategoryPageSectionWrapper>

                    <CategoryPageSectionWrapper
                        backgroundColor={theme.indigo20}
                        backgroundTexture={
                            <UnscoredCategoryPageBackgroundTextureCardTypesSection />
                        }
                        hasWave
                        id="types-of-card"
                        supportingText="There are a number of different types of credit card on the market, such as:"
                        title="Types of credit card"
                        waveColor={theme.indigo20}
                    >
                        <CategoryPageTypesSection />
                    </CategoryPageSectionWrapper>

                    <CategoryPageSectionWrapper
                        backgroundColor="white"
                        backgroundTexture={
                            <UnscoredCategoryPageBackgroundTextureProsAndConsSection />
                        }
                        hasWave
                        id="pros-and-cons"
                        supportingText="There are advantages and disadvantages to having credit cards, such as:"
                        title="What are the pros and cons of rewards credit cards?"
                        waveColor="white"
                    >
                        <CategoryPageProsAndConsSection page={page} />
                    </CategoryPageSectionWrapper>

                    <CategoryPageSectionWrapper
                        backgroundColor={theme.indigo10}
                        backgroundTexture={
                            <UnscoredCategoryPageBackgroundTextureExpertSection />
                        }
                        hasWave
                        waveColor={theme.indigo10}
                    >
                        <CategoryPageExpertSection page={page} />
                    </CategoryPageSectionWrapper>

                    <CategoryPageSectionWrapper
                        backgroundColor="white"
                        hasWave
                        id="find-the-right-card"
                        title="How to find the right rewards card"
                        supportingText="Finding the best rewards card depends on where you actually spend your money, how much you spend, and if you’re able to clear the balance in full every month."
                        waveColor="white"
                    >
                        <CategoryPageFindingTheRightCardSection page={page} />
                    </CategoryPageSectionWrapper>

                    <CategoryPageSectionWrapper
                        backgroundColor={theme.indigo10}
                        hasWave
                        id="why-totallymoney"
                        title="Why use TotallyMoney"
                        supportingText="With TotallyMoney, you can compare credit cards from across the UK market and check your eligibility before making an application. This will help protect your credit score as you can then only apply for cards you’re likely to be accepted for. Too many rejections can have a negative impact on your credit score."
                        waveColor={theme.indigo10}
                    >
                        <CategoryPageWhyTotallyMoneySection />
                    </CategoryPageSectionWrapper>

                    <CategoryPageSectionWrapper
                        backgroundColor="white"
                        hasWave
                        waveColor="white"
                    >
                        <CategoryPageFaqsSection page={page} />
                    </CategoryPageSectionWrapper>

                    <CategoryPageSectionWrapper
                        backgroundColor={theme.indigo10}
                        hasWave
                        waveColor={theme.indigo10}
                    >
                        <CategoryPageRelatedArticles page={page} />
                    </CategoryPageSectionWrapper>

                    <CategoryPageSectionWrapper
                        backgroundColor={theme.indigo20}
                        backgroundTexture={
                            <UnscoredCategoryPageBackgroundTextureReviewsSection />
                        }
                        hasWave
                        title="See why our customers love us"
                        waveColor={theme.indigo20}
                    >
                        <CategoryPageReviewsSection />
                    </CategoryPageSectionWrapper>
                </Stack>
            </Box>
        </>
    )
}

export default UnscoredRewardsPage
